import { zodResolver } from '@hookform/resolvers/zod'
import { CreateTrainingSchema } from '@lms/shared'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import type { z } from 'zod'
import { NotificationChannelSelectItems } from '@/components/training/NotificationChannelSelectItems'
import { ScheduleFormList } from '@/components/training/ScheduleFormList'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useCreateTraining, useTraining, useUpdateTraining } from '@/hooks/use-trainings'
import { fromDatetimeLocal, toDatetimeLocal } from '@/lib/datetime'
import { useTenantPath } from '@/lib/tenant'

type TrainingFormValues = z.infer<typeof CreateTrainingSchema>

interface ScheduleItem {
  id?: string
  scheduleDate: string
  endDate?: string
  location?: string
  dayNumber: number
}

export default function TrainingFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const tp = useTenantPath()
  const navigate = useNavigate()
  const { data: existing } = useTraining(id ?? '')
  const createTraining = useCreateTraining()
  const updateTraining = useUpdateTraining()

  const [schedules, setSchedules] = useState<ScheduleItem[]>([])
  const [isPublished, setIsPublished] = useState(false)

  const form = useForm<TrainingFormValues>({
    resolver: zodResolver(CreateTrainingSchema),
    defaultValues: {
      title: '',
      description: '',
      trainingType: 'open',
      category: '',
      tags: [],
      capacity: undefined,
      standardDurationHours: undefined,
      guideFileUrl: '',
      notificationChannel: undefined,
      hrContactEmail: '',
    },
  })

  useEffect(() => {
    if (existing && isEdit) {
      const t = existing as any
      form.reset({
        title: t.title ?? '',
        description: t.description ?? '',
        trainingType: t.trainingType ?? 'open',
        category: t.category ?? '',
        tags: t.tags ?? [],
        capacity: t.capacity ?? undefined,
        standardDurationHours: t.standardDurationHours ?? undefined,
        guideFileUrl: t.guideFileUrl ?? '',
        notificationChannel: t.notificationChannel ?? undefined,
        hrContactEmail: t.hrContactEmail ?? '',
      })
      setIsPublished(t.isPublished ?? false)
      if (t.schedules) {
        setSchedules(
          t.schedules.map((s: any) => ({
            id: s.id,
            scheduleDate: toDatetimeLocal(s.scheduleDate),
            endDate: toDatetimeLocal(s.endDate),
            location: s.location ?? '',
            dayNumber: s.dayNumber,
          })),
        )
      }
    }
  }, [existing, isEdit, form])

  const onSubmit = async (values: TrainingFormValues) => {
    console.log('[TrainingForm] submit values:', values)
    try {
      if (isEdit && id) {
        await updateTraining.mutateAsync({ id, data: { ...values, isPublished } as any })
        toast.success('研修を更新しました')
      } else {
        await createTraining.mutateAsync({
          ...values,
          // 入力欄の値はタイムゾーンを持たないため、送信前にオフセット付きへ変換する
          schedules: schedules.map((s) => ({
            scheduleDate: fromDatetimeLocal(s.scheduleDate),
            endDate: fromDatetimeLocal(s.endDate),
            location: s.location || undefined,
            dayNumber: s.dayNumber,
          })),
        } as any)
        toast.success('研修を作成しました')
      }
      navigate(tp('/teacher/trainings'))
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : isEdit ? '更新に失敗しました' : '作成に失敗しました',
      )
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Link to={tp('/teacher/trainings')}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-1 h-4 w-4" />
            戻る
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">{isEdit ? '研修編集' : '研修作成'}</h1>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (errors) =>
            console.error('[TrainingForm] validation errors:', errors),
          )}
          className="space-y-6"
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-base">基本情報</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>タイトル *</FormLabel>
                    <FormControl>
                      <Input placeholder="研修タイトル" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>説明</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="研修の説明"
                        rows={4}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="trainingType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>タイプ *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="タイプを選択" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="open">募集型</SelectItem>
                        <SelectItem value="assigned">アサイン型</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>カテゴリ</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="例: セキュリティ"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="capacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>定員</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="50"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value ? Number(e.target.value) : undefined)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="standardDurationHours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>標準受講時間（時間）</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.5"
                          placeholder="2"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value ? Number(e.target.value) : undefined)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="hrContactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>問い合わせメール</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="hr@example.com"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notificationChannel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>通知チャネル</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v === 'none' ? undefined : v)}
                      defaultValue={field.value ?? 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <NotificationChannelSelectItems />
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isEdit && (
                <div className="flex items-center gap-3">
                  <Switch checked={isPublished} onCheckedChange={setIsPublished} />
                  <span className="text-sm">{isPublished ? '公開中' : '下書き'}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {!isEdit && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">日程</CardTitle>
              </CardHeader>
              <CardContent>
                <ScheduleFormList schedules={schedules} onChange={setSchedules} />
              </CardContent>
            </Card>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={createTraining.isPending || updateTraining.isPending}
          >
            {createTraining.isPending || updateTraining.isPending
              ? '保存中...'
              : isEdit
                ? '更新する'
                : '作成する'}
          </Button>
        </form>
      </Form>
    </div>
  )
}
